import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import EmailIcon from '@mui/icons-material/Email';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShieldIcon from '@mui/icons-material/Shield';
import { useAuth } from '../../contexts/AuthContext';
import axiosInstance from '../../utils/axios';

const DIGIT_COUNT = 6;
const OTP_LENGTH = 6;

const PRIMARY = '#086428';
const PRIMARY_HOVER = '#064b1e';
const BG_LIGHT = '#f1f5f9';

type Method2FA = 'totp' | 'mail_otp';

/** Khi có cả totp và mail_otp: null = màn chọn phương thức, 'totp'|'mail_otp' = đang nhập mã */
type SelectedMethodScreen = null | Method2FA;

const RESEND_COOLDOWN_SEC = 600;
/** Countdown 10 phút khi lần đầu vào màn nhập mã Mail OTP */
const INITIAL_MAIL_OTP_COUNTDOWN_SEC = 600;

const TwoFA: React.FC = () => {
  const { user, tuvanNhathauUnitConfig, setTwoFaVerified } = useAuth();
  const navigate = useNavigate();
  const [digits, setDigits] = useState<string[]>(Array(DIGIT_COUNT).fill(''));
  const [error, setError] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  /** Countdown 10 phút khi lần đầu vào màn nhập mã Mail OTP (giây còn lại, 0 = hết hoặc chưa bắt đầu) */
  const [initialMailOtpCountdown, setInitialMailOtpCountdown] = useState(0);
  /** Khi có cả 2 phương thức: null = show box chọn, 'totp'|'mail_otp' = show box nhập mã tương ứng */
  const [selectedMethod, setSelectedMethod] = useState<SelectedMethodScreen>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const hasStartedInitialCountdown = useRef(false);

  // Đếm ngược sau khi gửi lại mã OTP
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const enabled2fa = (tuvanNhathauUnitConfig?.enabled_2fa || '').toLowerCase();
  const hasTotp = enabled2fa.includes('totp');
  const hasMailOtp = enabled2fa.includes('mail_otp');
  const canChooseMethod = hasTotp && hasMailOtp;
  /** Phương thức đang dùng để nhập mã (và gửi API). Khi canChooseMethod và đang ở màn chọn thì chưa có. */
  const effectiveMethod: Method2FA | null = canChooseMethod
    ? (selectedMethod as Method2FA | null)
    : (hasTotp ? 'totp' : 'mail_otp');
  const isChoiceScreen = canChooseMethod && selectedMethod === null;

  // Khi lần đầu chuyển đến màn nhập mã Mail OTP: bắt đầu countdown 10 phút
  useEffect(() => {
    if (effectiveMethod !== 'mail_otp' || isChoiceScreen) return;
    if (hasStartedInitialCountdown.current) return;
    hasStartedInitialCountdown.current = true;
    setInitialMailOtpCountdown(INITIAL_MAIL_OTP_COUNTDOWN_SEC);
  }, [effectiveMethod, isChoiceScreen]);

  // Tick countdown 10 phút mỗi giây
  useEffect(() => {
    if (initialMailOtpCountdown <= 0) return;
    const t = setInterval(() => setInitialMailOtpCountdown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearInterval(t);
  }, [initialMailOtpCountdown]);

  // Gửi mã OTP qua email lần đầu khi chuyển sang màn nhập mail_otp
  const hasSentInitialMailOtp = useRef(false);
  useEffect(() => {
    if (effectiveMethod !== 'mail_otp' || hasSentInitialMailOtp.current) return;
    hasSentInitialMailOtp.current = true;
    axiosInstance.post('/auth/send-mail-otp').catch(() => {
      hasSentInitialMailOtp.current = false;
    });
  }, [effectiveMethod]);

  const handleDigitChange = useCallback((index: number, value: string) => {
    if (value.length > 1) {
      value = value.slice(-1);
    }
    if (value !== '' && !/^\d$/.test(value)) return;
    const next = [...digits];
    next[index] = value;
    setDigits(next);
    setError('');
    if (value && index < DIGIT_COUNT - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }, [digits]);

  const handleKeyDown = useCallback((index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      const next = [...digits];
      next[index - 1] = '';
      setDigits(next);
    }
  }, [digits]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, DIGIT_COUNT);
    if (!pasted) return;
    const next = [...digits];
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted[i];
    }
    setDigits(next);
    setError('');
    const focusIndex = Math.min(pasted.length, DIGIT_COUNT) - 1;
    inputRefs.current[focusIndex]?.focus();
  }, [digits]);

  const handleSubmit = async () => {
    if (effectiveMethod === null) return;
    const code = digits.join('');
    if (code.length !== OTP_LENGTH) {
      setError('Vui lòng nhập đủ 6 số.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const res = await axiosInstance.post('/auth/two-fa-verify', {
        code,
        method: effectiveMethod
      });
      if (res.data?.code === 0) {
        setTwoFaVerified(true);
        const isTuvanNhathau = user?.tuvan_nhathau === 1;
        navigate(isTuvanNhathau ? '/tuvan_nhathau' : '/dashboard', { replace: true });
      } else {
        setError(res.data?.msg || 'Xác thực thất bại.');
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { msg?: string } } })?.response?.data?.msg;
      setError(msg || 'Xác thực thất bại.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0 || resendLoading) return;
    setResendLoading(true);
    setError('');
    try {
      const res = await axiosInstance.post('/auth/send-mail-otp');
      if (res.data?.code === 0) {
        setDigits(Array(DIGIT_COUNT).fill(''));
        inputRefs.current[0]?.focus();
        setResendCooldown(RESEND_COOLDOWN_SEC);
        setInitialMailOtpCountdown(INITIAL_MAIL_OTP_COUNTDOWN_SEC);
      } else {
        setError(res.data?.msg || 'Gửi lại mã thất bại.');
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { msg?: string } } })?.response?.data?.msg;
      setError(msg || 'Gửi lại mã thất bại.');
    } finally {
      setResendLoading(false);
    }
  };

  const chooseMethod = (method: Method2FA) => {
    setSelectedMethod(method);
    setError('');
    setDigits(Array(DIGIT_COUNT).fill(''));
  };

  const pageBg = BG_LIGHT;
  const cardBg = '#fff';
  const cardBorder = '1px solid #f3f4f6';
  const textPrimary = '#111827';
  const textSecondary = '#6b7280';
  const footerBg = '#f9fafb';
  const footerBorder = '1px solid #f3f4f6';
  const greenBoxBg = '#f0fdf4';
  const greenBoxBorder = '1px solid #dcfce7';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: pageBg,
        p: 2,
        transition: 'background-color 0.2s'
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 440,
          borderRadius: '16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 12px 32px -8px rgba(0,0,0,0.08)',
          overflow: 'hidden',
          border: cardBorder,
          bgcolor: cardBg,
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}
      >
        <Box sx={{ pt: 5, px: { xs: 3, sm: 5 }, pb: 4 }}>
          {/* Logo + PMIS */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}
              >
                <img src="/logo.png" alt="PMIS" style={{ height: 36, width: 'auto', maxWidth: 36, objectFit: 'contain', display: 'block' }} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: PRIMARY, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                  PMIS
                </Typography>
                <Typography
                  sx={{
                    fontSize: 10,
                    textTransform: 'uppercase',
                    letterSpacing: '0.18em',
                    fontWeight: 600,
                    color: '#94a3b8',
                    mt: 0.5
                  }}
                >
                  Enterprise System
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Chỉ hiện "Xác thực hai bước" khi đang chọn phương thức; ẩn khi nhập mã */}
          {isChoiceScreen && (
            <Typography
              component="h1"
              sx={{
                textAlign: 'center',
                fontSize: '1.375rem',
                fontWeight: 600,
                color: textPrimary,
                mb: 1,
                letterSpacing: '-0.01em'
              }}
            >
              Xác thực hai bước
            </Typography>
          )}

          {/* Màn chọn phương thức */}
          {isChoiceScreen && (
            <>
              <Typography
                sx={{
                  textAlign: 'center',
                  color: textSecondary,
                  fontSize: 15,
                  lineHeight: 1.55,
                  maxWidth: 300,
                  mx: 'auto',
                  mb: 4
                }}
              >
                Chọn phương thức xác thực để tiếp tục truy cập vào hệ thống.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 360, mx: 'auto' }}>
                <Button
                  fullWidth
                  disableElevation
                  onClick={() => chooseMethod('totp')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 72,
                    py: 2,
                    px: 2.5,
                    bgcolor: PRIMARY,
                    color: '#fff',
                    borderRadius: '12px',
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    boxShadow: '0 2px 8px rgba(8,100,40,0.25)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: PRIMARY_HOVER,
                      boxShadow: '0 4px 12px rgba(8,100,40,0.3)',
                      transform: 'translateY(-1px)'
                    },
                    '&:focus': {
                      outline: `2px solid ${PRIMARY}`,
                      outlineOffset: 2
                    }
                  }}
                >
                  <Box sx={{ width: 44, height: 44, borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2, flexShrink: 0 }}>
                    <SmartphoneIcon sx={{ color: '#fff', fontSize: 26 }} />
                  </Box>
                  <Box sx={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                    <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>Xác thực bằng TOTP</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, lineHeight: 1.35, mt: 0.25 }}>
                      Sử dụng ứng dụng Authenticator
                    </Typography>
                  </Box>
                  <ChevronRightIcon sx={{ color: 'rgba(255,255,255,0.6)', flexShrink: 0, fontSize: 28 }} />
                </Button>
                <Button
                  fullWidth
                  disableElevation
                  onClick={() => chooseMethod('mail_otp')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 72,
                    py: 2,
                    px: 2.5,
                    bgcolor: PRIMARY,
                    color: '#fff',
                    borderRadius: '12px',
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    boxShadow: '0 2px 8px rgba(8,100,40,0.25)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: PRIMARY_HOVER,
                      boxShadow: '0 4px 12px rgba(8,100,40,0.3)',
                      transform: 'translateY(-1px)'
                    },
                    '&:focus': {
                      outline: `2px solid ${PRIMARY}`,
                      outlineOffset: 2
                    }
                  }}
                >
                  <Box sx={{ width: 44, height: 44, borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2, flexShrink: 0 }}>
                    <EmailIcon sx={{ color: '#fff', fontSize: 26 }} />
                  </Box>
                  <Box sx={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                    <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>Xác thực bằng Mail OTP</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, lineHeight: 1.35, mt: 0.25 }}>
                      Mã OTP gửi về email đăng ký
                    </Typography>
                  </Box>
                  <ChevronRightIcon sx={{ color: 'rgba(255,255,255,0.6)', flexShrink: 0, fontSize: 28 }} />
                </Button>
              </Box>
            </>
          )}

          {/* Màn nhập mã 6 số (TOTP hoặc Mail OTP) */}
          {!isChoiceScreen && effectiveMethod !== null && (
            <>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography
                  component="h1"
                  sx={{
                    fontSize: '1.375rem',
                    fontWeight: 600,
                    color: textPrimary,
                    mb: 1.5,
                    letterSpacing: '-0.01em'
                  }}
                >
                  Nhập mã xác thực
                </Typography>
                {effectiveMethod === 'mail_otp' ? (
                  <Typography sx={{ color: textSecondary, fontSize: 15, lineHeight: 1.55 }}>
                    Chúng tôi đã gửi mã xác thực đến:
                    <br />
                    <Box component="span" sx={{ fontWeight: 600, color: '#334155' }}>
                      {user?.email || 'email của bạn'}
                    </Box>
                  </Typography>
                ) : (
                  <Typography sx={{ color: textSecondary, fontSize: 15, lineHeight: 1.55 }}>
                    Nhập mã 6 số từ ứng dụng Google Authenticator.
                  </Typography>
                )}
              </Box>

              <Box sx={{ maxWidth: 360, mx: 'auto' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    mb: 4
                  }}
                  onPaste={handlePaste}
                >
                  {Array.from({ length: DIGIT_COUNT }).map((_, i) => (
                    <TextField
                      key={i}
                      inputRef={el => { inputRefs.current[i] = el; }}
                      value={digits[i]}
                      onChange={e => handleDigitChange(i, e.target.value)}
                      onKeyDown={e => handleKeyDown(i, e)}
                      inputProps={{
                        maxLength: 1,
                        type: 'text',
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        style: { textAlign: 'center', fontSize: '1.25rem', fontWeight: 700 }
                      }}
                      sx={{
                        width: 48,
                        '& .MuiOutlinedInput-root': {
                          height: 56,
                          borderRadius: '10px',
                          bgcolor: '#fff',
                          transition: 'border-color 0.2s, box-shadow 0.2s',
                          '& fieldset': {
                            borderWidth: 2,
                            borderColor: digits[i] ? PRIMARY : '#e2e8f0'
                          },
                          '&:hover fieldset': {
                            borderColor: digits[i] ? PRIMARY : '#cbd5e1'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: PRIMARY,
                            borderWidth: 2
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${PRIMARY}30`
                          }
                        },
                        '& .MuiInputBase-input': {
                          color: textPrimary
                        }
                      }}
                    />
                  ))}
                </Box>

                <Button
                  fullWidth
                  disableElevation
                  disabled={submitting || digits.join('').length !== OTP_LENGTH}
                  onClick={handleSubmit}
                  sx={{
                    minHeight: 48,
                    py: 1.75,
                    borderRadius: '12px',
                    fontWeight: 600,
                    fontSize: 15,
                    bgcolor: PRIMARY,
                    color: '#fff !important',
                    boxShadow: '0 2px 8px rgba(8,100,40,0.25)',
                    mb: 3,
                    transition: 'all 0.2s ease',
                    '&:hover:not(:disabled)': {
                      bgcolor: PRIMARY_HOVER,
                      boxShadow: '0 4px 12px rgba(8,100,40,0.3)',
                      transform: 'translateY(-1px)',
                      color: '#fff !important'
                    },
                    '&:focus': {
                      outline: `2px solid ${PRIMARY}`,
                      outlineOffset: 2
                    },
                    '&.Mui-disabled': {
                      color: 'rgba(255,255,255,0.9) !important'
                    }
                  }}
                >
                  {submitting ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Xác nhận'}
                </Button>

                {effectiveMethod === 'mail_otp' && (
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography component="p" sx={{ fontSize: 14, color: textSecondary }}>
                      Bạn chưa nhận được mã?{' '}
                      <Link
                        component="button"
                        type="button"
                        disabled={resendLoading || resendCooldown > 0}
                        onClick={handleResendOtp}
                        sx={{
                          color: PRIMARY,
                          fontWeight: 600,
                          cursor: resendCooldown > 0 || resendLoading ? 'not-allowed' : 'pointer',
                          transition: 'opacity 0.2s',
                          '&:hover:not(:disabled)': { textDecoration: 'underline' },
                          '&:disabled': { color: textSecondary, opacity: 0.8 }
                        }}
                      >
                        {resendLoading ? 'Đang gửi...' : 'Gửi lại mã'}
                      </Link>
                      {initialMailOtpCountdown > 0 && (
                        <Box component="span" sx={{ color: textSecondary, fontWeight: 500, ml: 0.5 }}>
                          {' '}
                          ({Math.floor(initialMailOtpCountdown / 60)}:{String(initialMailOtpCountdown % 60).padStart(2, '0')})
                        </Box>
                      )}
                    </Typography>
                  </Box>
                )}

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                  </Alert>
                )}

              </Box>
            </>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            py: 2.5,
            px: 3,
            borderTop: footerBorder,
            bgcolor: footerBg,
            textAlign: 'center'
          }}
        >
          {!isChoiceScreen && canChooseMethod ? (
            <Link
              component="button"
              type="button"
              onClick={() => { setSelectedMethod(null); setError(''); setDigits(Array(DIGIT_COUNT).fill('')); }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                fontSize: 14,
                fontWeight: 500,
                color: textSecondary,
                cursor: 'pointer',
                border: 'none',
                background: 'none',
                p: 0,
                transition: 'color 0.2s',
                '&:hover': { color: PRIMARY }
              }}
            >
              <ShieldIcon sx={{ fontSize: 20 }} />
              Sử dụng phương thức khác
            </Link>
          ) : (
            <Link
              component={RouterLink}
              to="/login"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                fontSize: 14,
                fontWeight: 500,
                color: textSecondary,
                textDecoration: 'none',
                transition: 'color 0.2s',
                '&:hover': { color: PRIMARY }
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 20 }} />
              Quay lại đăng nhập
            </Link>
          )}
        </Box>
      </Paper>

      {/* Copyright */}
      <Box sx={{ mt: 3, textAlign: 'center', flexShrink: 0 }}>
        <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
          © 2024 PMIS Enterprise System. Bảo mật và An toàn.
        </Typography>
      </Box>
    </Box>
  );
};

export default TwoFA;
